import { RawDeviceHardware, VelavuAPI, VelavuDeviceModel } from "velavu-js-api";
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

export interface DeviceModelProps {
	deviceModels: readonly VelavuDeviceModel[] | null;
	getDeviceModel: (
		model: string | undefined,
	) => Readonly<VelavuDeviceModel> | undefined;
}

export const DeviceModelContext = createContext<DeviceModelProps>({
	deviceModels: [],
	getDeviceModel: () => undefined,
});

export interface DeviceModelProviderProps {
	children: React.ReactNode;
}

//// This component fetches the device models from the server and provides them to the rest of the app
export function DeviceModelProvider(props: DeviceModelProviderProps) {
	//Fetch device models from the server
	const [deviceModelArray, setDeviceModelArray] = useState<
		VelavuDeviceModel[] | null
	>(null);
	useEffect(() => {
		VelavuAPI.deviceModels
			.getAllDeviceModels()
			.then((deviceModelArray) => {
				setDeviceModelArray(deviceModelArray);
			})
			.catch((err) => {
				console.warn("Error fetching device models", err);
			});
	}, [setDeviceModelArray]);

	const deviceModelMap = useMemo(() => {
		if (deviceModelArray === null) {
			return null;
		}

		//Collect device models in a map
		const deviceModelMap = new Map<RawDeviceHardware, VelavuDeviceModel>();
		for (const deviceModel of deviceModelArray) {
			deviceModelMap.set(deviceModel.name, deviceModel);
		}
		return deviceModelMap;
	}, [deviceModelArray]);

	const lookUpDeviceModels = useCallback(
		(model: string | undefined) => {
			if (deviceModelMap === null || model === undefined) {
				return undefined;
			}
			return deviceModelMap.get(model);
		},
		[deviceModelMap],
	);

	const value = useMemo<DeviceModelProps>(
		() => ({
			deviceModels: deviceModelArray,
			getDeviceModel: lookUpDeviceModels,
		}),
		[deviceModelArray, lookUpDeviceModels],
	);

	return (
		<DeviceModelContext value={value}>{props.children}</DeviceModelContext>
	);
}

export function useDeviceModels() {
	return useContext(DeviceModelContext);
}
